import profile from '@/utils/profile'

const CLASSROOM_BASE = 'https://classroom.googleapis.com/v1'
const TOKENINFO_URL = 'https://oauth2.googleapis.com/tokeninfo'
// Drive scope 從 profile.googleScopes.active 取，統一在 profile.js 管理
const DRIVE_FILE_SCOPE = profile.googleScopes.active.find(s => s.includes('drive.file'))

function loadGapiPicker() {
    return new Promise((resolve, reject) => {
        if (window.gapi && window.gapi.load) {
            window.gapi.load('picker', { callback: resolve, onerror: reject })
            return
        }
        const script = document.createElement('script')
        script.src = 'https://apis.google.com/js/api.js'
        script.onload = () => window.gapi.load('picker', { callback: resolve, onerror: reject })
        script.onerror = reject
        document.head.appendChild(script)
    })
}

async function downloadDriveFile(fileId, accessToken) {
    const resp = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
    )
    if (!resp.ok) throw new Error(`Drive 下載失敗 (HTTP ${resp.status})`)
    return resp.blob()
}

async function exportSlidesAsPdf(fileId, accessToken) {
    const resp = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
    )
    if (resp.status === 403) {
        const body = await resp.json().catch(() => null)
        const reason = body?.error?.errors?.[0]?.reason ?? 'forbidden'
        throw Object.assign(new Error(reason), { code: 'EXPORT_FORBIDDEN', reason })
    }
    if (!resp.ok) throw new Error(`Slides PDF 匯出失敗 (HTTP ${resp.status})`)
    return resp.blob()
}

async function gFetch(url, accessToken, retryFn, retried = false) {
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
    if (resp.status === 401 && !retried && retryFn) {
        // token 過期，重新取得後 retry 一次
        const newToken = await retryFn()
        return gFetch(url, newToken, retryFn, true)
    }
    const data = await resp.json()
    if (!resp.ok) throw new Error(data.error?.message || JSON.stringify(data.error) || String(resp.status))
    return data
}

export default {
    /**
     * 查詢 access_token 的 tokeninfo（含 scope、exp 等）
     * @param {string} accessToken
     * @returns {Promise<object>} tokeninfo，有 error 欄位時表示 token 失效
     */
    async getTokenInfo(accessToken) {
        const resp = await fetch(`${TOKENINFO_URL}?access_token=${accessToken}`)
        return resp.json()
    },

    /**
     * 取所有課程（自動分頁）
     * @param {string} accessToken
     * @param {Function} retryFn  token 過期時呼叫以取得新 token，回傳 Promise<string>
     */
    async getCourses(accessToken, retryFn) {
        let courses = []
        let pageToken = null
        do {
            const url = `${CLASSROOM_BASE}/courses?pageSize=50${pageToken ? '&pageToken=' + pageToken : ''}`
            const r = await gFetch(url, accessToken, retryFn)
            if (r.courses) courses = courses.concat(r.courses)
            pageToken = r.nextPageToken || null
        } while (pageToken)
        return courses
    },

    // 未使用 - 取得課程教師名單（保留供未來回寫 Classroom 成績用）
    // async getTeachers(courseId, accessToken, retryFn) {
    //     const url = `${CLASSROOM_BASE}/courses/${courseId}/teachers?pageSize=50`
    //     const r = await gFetch(url, accessToken, retryFn)
    //     return r.teachers || []
    // },

    async getStudents(courseId, accessToken, retryFn) {
        let students = []
        let pageToken = null
        do {
            const url = `${CLASSROOM_BASE}/courses/${courseId}/students?pageSize=50${pageToken ? '&pageToken=' + pageToken : ''}`
            const r = await gFetch(url, accessToken, retryFn)
            if (r.students) students = students.concat(r.students)
            pageToken = r.nextPageToken || null
        } while (pageToken)
        return students
    },

    /**
     * 檢查 accessToken 是否具備 Drive scope 且尚未過期
     * @param {string} accessToken
     * @returns {Promise<{valid: boolean, reason?: string}>}
     */
    async checkDriveToken(accessToken) {
        const info = await this.getTokenInfo(accessToken)
        if (info.error) return { valid: false, reason: 'expired' }
        const scopes = info.scope || ''
        if (!scopes.includes(DRIVE_FILE_SCOPE)) return { valid: false, reason: 'missing_scope' }
        return { valid: true }
    },

    /**
     * 取得或建立指定名稱的 Drive 資料夾，回傳資料夾 ID
     * （drive.file scope 下只能看到本 app 建立的資料夾）
     * @param {string} folderName
     * @param {string} accessToken
     * @param {string|null} parentId  父資料夾 ID，null 表示根目錄
     * @returns {Promise<string>} 資料夾 ID
     */
    async getOrCreateDriveFolder(folderName, accessToken, parentId = null) {
        const FOLDER_MIME = 'application/vnd.google-apps.folder'
        const parentClause = parentId ? ` and '${parentId}' in parents` : ''
        const escapedName = folderName.replace(/'/g, "\\'")
        const q = encodeURIComponent(`name='${escapedName}' and mimeType='${FOLDER_MIME}' and trashed=false${parentClause}`)
        const listResp = await fetch(
            `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id)&pageSize=1`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        )
        if (listResp.ok) {
            const listData = await listResp.json()
            if (listData.files && listData.files.length > 0) {
                return listData.files[0].id
            }
        }
        // 不存在則建立
        const body = { name: folderName, mimeType: FOLDER_MIME }
        if (parentId) body.parents = [parentId]
        const createResp = await fetch(
            'https://www.googleapis.com/drive/v3/files',
            {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        )
        if (!createResp.ok) {
            const err = await createResp.json().catch(() => null)
            throw new Error(err?.error?.message || `建立資料夾失敗 (HTTP ${createResp.status})`)
        }
        const folder = await createResp.json()
        return folder.id
    },

    /**
     * 上傳檔案到指定 Drive 資料夾
     * @param {Blob} blob
     * @param {string} fileName
     * @param {string} accessToken
     * @param {string} folderId  目標資料夾 ID
     * @returns {Promise<object>} 新建的 Drive 檔案 metadata
     */
    async uploadFileToDrive(blob, fileName, accessToken, folderId) {
        const metadata = {
            name: fileName,
            mimeType: blob.type || 'application/octet-stream',
            parents: [folderId],
        }
        const form = new FormData()
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }))
        form.append('file', blob, fileName)
        const resp = await fetch(
            'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
            { method: 'POST', headers: { Authorization: `Bearer ${accessToken}` }, body: form }
        )
        if (!resp.ok) {
            const err = await resp.json().catch(() => null)
            throw new Error(err?.error?.message || `Drive 上傳失敗 (HTTP ${resp.status})`)
        }
        return resp.json()
    },

    /**
     * 開啟 Google Picker 讓使用者選擇 PDF 或 Google Slides，
     * 選擇後自動下載並回傳 File 物件（application/pdf）
     * @param {string} accessToken    具備 drive.file scope 的 access token
     * @param {string} apiKey         Google API Key（Developer Key）
     * @param {object} [opts]
     * @param {Function} [opts.onDownloading]  開始下載前呼叫（可用來顯示 loading）
     * @returns {Promise<File|null>} 使用者選擇的檔案，取消則回傳 null
     */
    openDrivePicker(accessToken, apiKey, { onDownloading } = {}) {
        return new Promise((resolve, reject) => {
            loadGapiPicker().then(() => {
                const MIME_SLIDES = 'application/vnd.google-apps.presentation'

                // Tab 1：PDF
                const pdfView = new google.picker.DocsView(google.picker.ViewId.PDFS)
                    .setIncludeFolders(false)
                    .setLabel('PDF')

                // Tab 2：Slides
                const slidesView = new google.picker.DocsView(google.picker.ViewId.PRESENTATIONS)
                    .setMimeTypes(MIME_SLIDES)
                    .setIncludeFolders(false)
                    .setLabel('Slides')

                const picker = new google.picker.PickerBuilder()
                    .setOAuthToken(accessToken)
                    .setAppId('30604537950')
                    .setDeveloperKey(apiKey)
                    .setTitle('選擇 Google Drive 檔案')
                    .addView(pdfView)
                    .addView(slidesView)
                    .setCallback(async (data) => {
                        if (data.action === google.picker.Action.PICKED) {
                            const doc = data.docs[0]
                            try {
                                if (onDownloading) onDownloading(doc)
                                let blob
                                let fileName
                                if (doc.mimeType === 'application/pdf') {
                                    blob = await downloadDriveFile(doc.id, accessToken)
                                    fileName = doc.name.endsWith('.pdf') ? doc.name : doc.name + '.pdf'
                                } else {
                                    blob = await exportSlidesAsPdf(doc.id, accessToken)
                                    fileName = doc.name + '.pdf'
                                }
                                resolve(new File([blob], fileName, { type: 'application/pdf' }))
                            } catch (e) {
                                reject(e)
                            }
                        } else if (data.action === google.picker.Action.CANCEL) {
                            resolve(null)
                        }
                    })
                    .build()

                picker.setVisible(true)
            }).catch(reject)
        })
    },
}
